import { ContentCopy, SvgIconComponent, Warning } from '@mui/icons-material';
import { Stack, Typography, styled, Paper, ListItem, List, ListItemButton, ListItemIcon, Tooltip, Box } from '@mui/material';
import { FC } from 'react';
import { getPropertyIcon } from '../../settings/icons';

interface IProps 
{
    properties?: (IProperty|IObjects)[];
    onCopyValue?: (copied: boolean)=>void;
}

export interface IItem
{
    title: string;
    icon?: SvgIconComponent;
}

const Item = styled(Paper)(({ theme, color }) => ({
    backgroundColor: color,
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'left',
    margin: 5,
    color: theme.palette.text.secondary,
}));

const ItemList: FC<IProps> = ({ properties, onCopyValue }) => 
{
    const copyValue = (value: string)=>{
        navigator.clipboard.writeText(value).then(() => {
            if(onCopyValue) onCopyValue(true);
        }, (err) => {
            if(onCopyValue) onCopyValue(false);
        });
    }

    const renderProps = (props: (IProperty|IObjects)[]): JSX.Element => {
        return (<>
            {
                props.map((item: IProperty|IObjects, index: number) => {
                    if(isObjects(item))
                    {
                        return <Box key={`property-list-item-${item.id}`}>
                            {
                                item.objects.map(object=>{
                                    return renderProps(object.properties)
                                })
                            }
                        </Box>
                    }

                    const Icon = item.temporary ? Warning : item.icon ? item.icon : getPropertyIcon(item.type);
                    const propertyCard = (<Item color={item.temporary ? '#ffdfdf' : '#f7f7f7'}>
                        {
                            item.title !== undefined && item.title !== "" ?
                                <Typography variant='subtitle1' sx={{fontWeight: 5}}>
                                    <Icon sx={{verticalAlign: 'middle', mr: "3px", pb: "6px", pt: "2px"}} />
                                    {item.title}
                                </Typography> :
                                <></>
                        }
                        <List sx={{lineBreak: 'anywhere', ml: 0}}>
                            {
                                Array.isArray(item.value) ? 
                                    item.value.map(value=>(
                                        <ListItem disablePadding sx={{ml: "-8px", width: "calc(100% + 16px)", padding: 0}}>
                                            <ListItemButton onClick={()=>copyValue(value)}>
                                                <ListItemIcon>
                                                    <ContentCopy/>
                                                </ListItemIcon>
                                                {value}
                                            </ListItemButton>
                                        </ListItem>
                                    )) :
                                    <ListItem sx={{ml: "-8px", width: "calc(100% + 16px)", padding: 0}}>
                                        <ListItemButton onClick={()=>copyValue(item.value as string)}>
                                            <ListItemIcon>
                                                <ContentCopy/>
                                            </ListItemIcon>
                                            {item.value as string}
                                        </ListItemButton>
                                    </ListItem>
                            }
                        </List>
                    </Item>);
                    return (<Box key={`property-list-item-${item.id}`}>
                        {
                            item.temporary ?
                                <Tooltip title="This is a temporary property, make sure to copy it or it will be lost and not retrievable.">{propertyCard}</Tooltip> :
                                propertyCard
                        }
                    </Box>)
                })
            }
        </>);
    }

    return properties ? <Stack>{renderProps(properties)}</Stack> : <></>;
};

const isObjects = (objects: IProperty | IObjects): objects is IObjects =>
{
    return (objects as IObjects).objects !== undefined;
}

export default ItemList;